import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { LogOut, KeyRound, BookOpen, FileText, ClipboardList, LifeBuoy, AlertTriangle, Heart, MessageCircle } from 'lucide-react';
import EmergencyContactsCard from '../components/EmergencyContactsCard';
import PageHeader from '../components/PageHeader';
import ManagePasskeysCard from '../components/ManagePasskeysCard';
import AvatarPicker from '../components/AvatarPicker';
import SobrietyClockColorPicker from '../components/SobrietyClockColorPicker';
import { SUPPORT_DIRECTORY } from '../data/supportDirectory';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';
import useAllowPrint from '../components/useAllowPrint';

function ProfilePage() {
  useAllowPrint();
  useTheme4A();
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <PageHeader title="Profile" />

      <main className="pt-20 pb-6 px-4">
        <div className="max-w-md mx-auto">
          {/* Medical Disclaimer: pulled to the top so a new user cannot miss
              it. Amber warning styling matches the app's clinical alerts. */}
          <div
            className="mb-6 rounded-md border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="medical-disclaimer"
            role="note"
          >
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-900 mb-1">Medical Disclaimer</p>
              <p className="text-sm text-amber-900 leading-relaxed">
                This tool is an aid for Recovery and does <strong>not</strong> replace
                professional medical advice or supervised detoxification. If you are in
                crisis, contact your GP, a mental-health professional or your local
                emergency service immediately.
              </p>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
            <div className="mb-6">
              <AvatarPicker user={user} onUpdated={checkAuth} />
              <div className="mt-4">
                <h2 className="text-xl font-bold text-zinc-950">{user?.name}</h2>
                <p className="text-sm text-zinc-500">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-zinc-200">
              <div className="flex justify-between">
                <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Current Week</span>
                <span className="text-sm font-semibold text-zinc-950">{user?.week || 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Sponsor Bot</span>
                <span className="text-sm font-semibold text-zinc-950">{user?.bot_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Language</span>
                <span className="text-sm font-semibold text-zinc-950">{user?.language?.toUpperCase()}</span>
              </div>
              <CountryRow user={user} onSaved={checkAuth} />
            </div>

            <SobrietyClockColorPicker user={user} onUpdated={checkAuth} />
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/personal-history')}
              variant="outline"
              className="w-full h-12"
              data-testid="personal-history-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Personal History
            </Button>

            <Button
              onClick={() => navigate('/case-management')}
              variant="outline"
              className="w-full h-12"
              data-testid="case-management-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Case Management
            </Button>

            <Button
              onClick={() => navigate('/set-pin')}
              variant="outline"
              className="w-full h-12"
              data-testid="set-pin-btn"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {user?.has_pin ? 'Change PIN' : 'Set PIN'}
            </Button>
          </div>

          {/* Sign-in security: PIN + Passkeys */}
          <div className="mt-3">
            <ManagePasskeysCard />
          </div>

          {/* Contact Person / Family: mental-health crisis contacts */}
          <div className="mt-3">
            <EmergencyContactsCard />
          </div>

          <div className="space-y-3 mt-3">

            <Button
              onClick={() => navigate('/onboarding')}
              variant="outline"
              className="w-full h-12"
              data-testid="edit-preferences-btn"
            >
              Edit Preferences
            </Button>

            <Button
              onClick={() => navigate('/user-manual')}
              variant="outline"
              className="w-full h-12"
              data-testid="user-manual-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              User Manual
            </Button>

            <Button
              onClick={() => navigate('/relapse-recovery')}
              variant="outline"
              className="w-full h-12 text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="relapse-recovery-btn"
            >
              <LifeBuoy className="w-4 h-4 mr-2" />
              Relapse Recovery
            </Button>

            {(user?.gender === 'female' || user?.role === 'superadmin') && (
              <Button
                onClick={() => navigate('/women-only')}
                variant="outline"
                className="w-full h-12 text-rose-700 border-rose-200 hover:bg-rose-50"
                data-testid="women-only-btn"
              >
                <Heart className="w-4 h-4 mr-2" />
                Women-Only Safe Space
              </Button>
            )}

            {(user?.gender === 'male' || user?.role === 'superadmin') && (
              <Button
                onClick={() => navigate('/men-only')}
                variant="outline"
                className="w-full h-12 text-sky-700 border-sky-200 hover:bg-sky-50"
                data-testid="men-only-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Men-Only Safe Space
              </Button>
            )}

            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full h-12 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function CountryRow({ user, onSaved }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [editing, setEditing] = useState(false);
  const [cc, setCc] = useState((user?.country || '').toUpperCase());
  const [busy, setBusy] = useState(false);
  const label = user?.country || 'Not set';

  const save = async () => {
    if (!cc) return;
    setBusy(true);
    try {
      await authFetch(`${apiBase}/api/user/country`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: cc }),
      });
      try { localStorage.setItem('user_country_code', cc); } catch (_) { /* private mode */ }
      if (onSaved) await onSaved();
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex justify-between items-center" data-testid="profile-country-row">
        <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Country</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-950" data-testid="profile-country-value">{label}</span>
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-blue-700 hover:text-blue-900 underline"
            data-testid="profile-country-edit"
          >
            {user?.country ? 'Change' : 'Set'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="profile-country-editor">
      <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider block">Country</span>
      <select
        value={cc}
        onChange={(e) => setCc(e.target.value)}
        className="w-full border border-zinc-300 rounded-md px-2 py-1.5 text-sm bg-white"
        data-testid="profile-country-select"
      >
        <option value="">Choose your country</option>
        {SUPPORT_DIRECTORY.map(c => (
          <option key={c.country_code} value={c.country_code}>{c.country_name}</option>
        ))}
      </select>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={!cc || busy}
          className="flex-1 px-3 py-1.5 text-sm font-bold bg-zinc-900 hover:bg-zinc-700 text-white rounded-md disabled:opacity-50"
          data-testid="profile-country-save"
        >
          {busy ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={() => { setEditing(false); setCc((user?.country || '').toUpperCase()); }}
          className="px-3 py-1.5 text-sm text-zinc-700 hover:text-zinc-900 underline"
          data-testid="profile-country-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default ProfilePage;